/**
 * @fileoverview Shared inspection template rule-authoring side panel.
 * @module template-builder/CanvasRulesPanel
 *
 * Responsibilities:
 * - Render template-level and element-level rule lists.
 * - Provide a guided rule builder for conditions and rule actions.
 * - Persist rule edits through host-supplied local-first template callbacks.
 *
 * Dependencies:
 * - React local state.
 * - lucide-react icons.
 * - Canonical template builder rule types.
 *
 * Architecture:
 * - Presentation-only authoring component; host pages own Dexie reads and writes.
 * - No API, fetch, storage, or mock-data access is performed here.
 *
 * Notes:
 * - Keep rule action coverage in sync with ActionType and Action contracts.
 */
import { useState } from 'react';
import { X, Plus, Pencil, Trash2, Check, AlertTriangle, Info, ChevronRight, Pin, PinOff } from 'lucide-react';
import type { PanelSelection } from '../form-builder/PropertiesPanel';
import type {
  TemplateBuilderTemplate, TemplateBuilderForm, Rule, Condition, Action,
  Operator, ActionType,
} from '../../types/templateTypes';
import { OPERATOR_LABELS } from '../../types/templateTypes';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RULE_ELIGIBLE_FIELD_TYPES = new Set([
  'toggle', 'dropdown', 'radioGroup', 'checkboxGroup', 'number', 'measurement',
]);

// ---------------------------------------------------------------------------
// CanvasRulesPanel props
// ---------------------------------------------------------------------------

export interface CanvasRulesPanelProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
  onUpdate: (updates: Partial<TemplateBuilderTemplate>) => void;
  onClose: () => void;
  selection?: PanelSelection;
  pinned?: boolean;
  onPin?: () => void;
  panelWidth?: number;
}

// ---------------------------------------------------------------------------
// CanvasRulesPanel
// ---------------------------------------------------------------------------

export function CanvasRulesPanel({
  template,
  forms,
  onUpdate,
  onClose,
  selection,
  pinned,
  onPin,
  panelWidth,
}: CanvasRulesPanelProps) {
  const rules = template.rules || [];
  const [activeTab, setActiveTab] = useState<'element' | 'canvas'>('canvas');

  const [showBuilder, setShowBuilder] = useState(false);
  const [wizardStep, setWizardStep] = useState<0 | 1 | 2 | 3>(0);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [sourceFormId, setSourceFormId] = useState('');
  const [sourceFieldId, setSourceFieldId] = useState('');
  const [operator, setOperator] = useState<Operator>('equals');
  const [conditionValue, setConditionValue] = useState<any>('');
  const [actionType, setActionType] = useState<ActionType>('hideForm');
  const [targetFormId, setTargetFormId] = useState('');
  const [targetFieldId, setTargetFieldId] = useState('');
  const [targetSectionId, setTargetSectionId] = useState('');
  const [setOptionsText, setSetOptionsText] = useState('');
  const [requireNotes, setRequireNotes] = useState(false);
  const [requirePhotos, setRequirePhotos] = useState(false);

  const formList = template.formRefs
    .map((r) => ({ id: r.formId, form: forms.get(r.formId) }))
    .filter((f) => f.form);
  const formRuleEntries = formList.flatMap((entry) => (entry.form?.rules || []).map((rule) => ({
    rule,
    source: 'form' as const,
    formId: entry.id,
    formName: entry.form?.name || entry.id,
  })));
  const displayRules = [
    ...rules.map((rule) => ({ rule, source: 'template' as const, formId: undefined, formName: undefined })),
    ...formRuleEntries,
  ];

  const sourceForm = forms.get(sourceFormId);
  const sourceFields = sourceForm
    ? sourceForm.fields.filter((f) => RULE_ELIGIBLE_FIELD_TYPES.has(f.type))
    : [];
  const sourceField = sourceForm?.fields.find((f) => f.id === sourceFieldId);
  const targetForms = formList.filter((f) => f.id !== sourceFormId);
  const parseOptionsText = (text: string) => text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawValue, rawLabel] = line.includes('|') ? line.split('|', 2) : [line, line];
      return { value: rawValue.trim(), label: rawLabel.trim() };
    });

  const resolveField = (fieldId: string): string => {
    for (const [, form] of forms) {
      const field = form.fields.find((f) => f.id === fieldId);
      if (field) return `"${field.label}" on ${form.name}`;
    }
    return fieldId;
  };

  const summarizeCondition = (c: Condition): string => {
    if ('field' in c) {
      const opLabel = OPERATOR_LABELS[c.op] || c.op;
      const valLabel = typeof c.value === 'boolean'
        ? (c.value ? 'Yes' : 'No')
        : JSON.stringify(c.value ?? '');
      return `${resolveField(c.field)} ${opLabel} ${valLabel}`;
    }
    if ('and' in c) return c.and.map(summarizeCondition).join(' AND ');
    if ('or' in c) return c.or.map(summarizeCondition).join(' OR ');
    if ('not' in c) return `NOT (${summarizeCondition(c.not)})`;
    return '(unknown)';
  };

  const summarizeAction = (a: Action): string => {
    if (a.action === 'hideForm' || a.action === 'showForm') {
      const form = forms.get(a.formId);
      return `${a.action === 'hideForm' ? 'Hide' : 'Show'} "${form?.name || a.formId}"`;
    }
    if (a.action === 'hideSection' || a.action === 'showSection') {
      return `${a.action === 'hideSection' ? 'Hide' : 'Show'} section`;
    }
    if (a.action === 'hideField' || a.action === 'showField') {
      return `${a.action === 'hideField' ? 'Hide' : 'Show'} field`;
    }
    return a.action;
  };

  const resetBuilder = () => {
    setShowBuilder(false);
    setWizardStep(0);
    setEditingRuleId(null);
    setRuleName('');
    setSourceFormId('');
    setSourceFieldId('');
    setOperator('equals');
    setConditionValue('');
    setActionType('hideForm');
    setTargetFormId('');
    setTargetFieldId('');
    setTargetSectionId('');
    setSetOptionsText('');
    setRequireNotes(false);
    setRequirePhotos(false);
  };

  const openBuilder = (rule?: Rule) => {
    setWizardStep(0);
    if (rule) {
      setEditingRuleId(rule.id);
      setRuleName(rule.name || '');
      const cond = rule.when as { field?: string; op?: string; value?: any };
      if (cond.field) {
        for (const [, form] of forms) {
          const field = form.fields.find((f) => f.id === cond.field);
          if (field) { setSourceFormId(form._id); break; }
        }
        setSourceFieldId(cond.field);
      }
      setOperator((cond.op as Operator) || 'equals');
      setConditionValue(cond.value ?? '');
      const act = rule.then[0] as { action: string; formId?: string; fieldId?: string; sectionId?: string; options?: Array<{ value?: string; label?: string }>; requireNotes?: boolean; requirePhotos?: boolean };
      setActionType((act?.action as ActionType) || 'hideForm');
      setTargetFormId(act?.formId || '');
      setTargetFieldId(act?.fieldId || '');
      setTargetSectionId(act?.sectionId || '');
      setSetOptionsText((act?.options || []).map((option) => `${option.value ?? option.label ?? ''}|${option.label ?? option.value ?? ''}`).join('\n'));
      setRequireNotes(act?.requireNotes === true);
      setRequirePhotos(act?.requirePhotos === true);
    } else {
      resetBuilder();
      if (formList.length > 0) setSourceFormId(formList[0].id);
    }
    setShowBuilder(true);
  };

  const buildAction = (): Action | null => {
    if (actionType === 'hideForm' || actionType === 'showForm') {
      if (!targetFormId) return null;
      return actionType === 'hideForm'
        ? { action: 'hideForm', formId: targetFormId }
        : { action: 'showForm', formId: targetFormId };
    }
    if (actionType === 'hideField' || actionType === 'showField') {
      if (!targetFieldId) return null;
      return { action: actionType, fieldId: targetFieldId };
    }
    if (actionType === 'hideSection' || actionType === 'showSection') {
      if (!targetSectionId) return null;
      return { action: actionType, sectionId: targetSectionId };
    }
    if (actionType === 'require') {
      if (!targetFieldId) return null;
      return { action: 'require', fieldId: targetFieldId, requireNotes, requirePhotos };
    }
    if (actionType === 'setOptions') {
      if (!targetFieldId) return null;
      const options = parseOptionsText(setOptionsText);
      if (options.length === 0) return null;
      return { action: 'setOptions', fieldId: targetFieldId, options };
    }
    if (actionType === 'disable' || actionType === 'enable') {
      if (!targetFieldId) return null;
      return { action: actionType, fieldId: targetFieldId };
    }
    return null;
  };

  const handleSaveRule = () => {
    if (!sourceFieldId) return;
    const action = buildAction();
    if (!action) return;
    const condition: Condition = { field: sourceFieldId, op: operator, value: conditionValue };

    const newRule: Rule = {
      id: editingRuleId || `rule-${Date.now()}`,
      name: ruleName || undefined,
      scope: 'template',
      when: condition,
      then: [action],
    };

    const nextRules = editingRuleId
      ? rules.map((r) => r.id === editingRuleId ? newRule : r)
      : [...rules, newRule];

    const nextFormRefs = (actionType === 'hideForm' || actionType === 'showForm')
      ? template.formRefs.map((r) => r.formId === targetFormId ? { ...r, optional: true } : r)
      : template.formRefs;

    onUpdate({ rules: nextRules, formRefs: nextFormRefs });
    resetBuilder();
  };

  const handleDeleteRule = (ruleId: string) => {
    onUpdate({ rules: rules.filter((r) => r.id !== ruleId) });
  };

  const actionTargetReady = (() => {
    if (actionType === 'hideForm' || actionType === 'showForm') return !!targetFormId;
    if (actionType === 'hideField' || actionType === 'showField' || actionType === 'require' || actionType === 'disable' || actionType === 'enable') return !!targetFieldId;
    if (actionType === 'setOptions') return !!targetFieldId && parseOptionsText(setOptionsText).length > 0;
    if (actionType === 'hideSection' || actionType === 'showSection') return !!targetSectionId;
    return false;
  })();
  const conditionReady = operator === 'exists'
    || (Array.isArray(conditionValue) ? conditionValue.length > 0 : conditionValue !== '' && conditionValue !== undefined);
  const canSave = !!sourceFieldId && actionTargetReady && conditionReady;

  const renderValuePicker = () => {
    if (!sourceField) return null;
    if (sourceField.type === 'toggle') {
      return (
        <select
          value={String(conditionValue)}
          onChange={(e) => setConditionValue(e.target.value === 'true')}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
        >
          <option value="true">{sourceField.extras?.onLabel || 'Yes'}</option>
          <option value="false">{sourceField.extras?.offLabel || 'No'}</option>
        </select>
      );
    }
    if (['dropdown', 'radioGroup', 'checkboxGroup'].includes(sourceField.type)) {
      const options: { value: string; label: string }[] = sourceField.extras?.options || [];
      if (operator === 'in' || operator === 'notIn') {
        const selectedValues = Array.isArray(conditionValue) ? conditionValue : [];
        return (
          <div className="space-y-1 max-h-32 overflow-y-auto rounded-sm border border-gray-200 bg-white p-1.5">
            {options.map((option) => (
              <label key={option.value} className="flex items-center gap-1.5 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={(event) => {
                    const next = event.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter((value) => value !== option.value);
                    setConditionValue(next);
                  }}
                  className="rounded-sm border-gray-300"
                />
                {option.label}
              </label>
            ))}
          </div>
        );
      }
      return (
        <select
          value={String(conditionValue)}
          onChange={(e) => setConditionValue(e.target.value)}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
        >
          <option value="">Select value…</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (sourceField.type === 'number' || sourceField.type === 'measurement') {
      return (
        <input
          type="number"
          value={conditionValue ?? ''}
          onChange={(e) => setConditionValue(e.target.value !== '' ? Number(e.target.value) : '')}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
          placeholder="e.g. 100"
        />
      );
    }
    return (
      <input
        type="text"
        value={conditionValue ?? ''}
        onChange={(e) => setConditionValue(e.target.value)}
        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
        placeholder="Value"
      />
    );
  };

  return (
    <div
      className="h-full border-l bg-white flex flex-col overflow-hidden"
      style={panelWidth ? { width: panelWidth } : undefined}
    >
      {/* Header */}
      <div className="shrink-0 px-3 py-2.5 border-b bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChevronRight size={13} className="text-gray-400" />
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Rules</h3>
          {rules.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{rules.length}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!showBuilder && formList.length >= 2 && activeTab === 'canvas' && (
            <button
              onClick={() => openBuilder()}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white rounded-sm"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              <Plus size={9} /> Add
            </button>
          )}
          {onPin && (
            <button onClick={onPin} title={pinned ? 'Unpin panel' : 'Pin panel open'} className="p-0.5 text-gray-400 hover:text-gray-600 rounded-sm">
              {pinned ? <PinOff size={12} /> : <Pin size={12} />}
            </button>
          )}
          {!pinned && (
            <button onClick={onClose} className="p-0.5 text-gray-400 hover:text-gray-600 rounded-sm">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="shrink-0 flex border-b bg-white">
        {(['canvas', 'element'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-1.5 text-[10px] font-medium transition-colors ${
              activeTab === tab
                ? 'text-(--brand-primary) border-b-2 border-(--brand-primary)'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {tab === 'canvas' ? 'Canvas Rules' : 'Element'}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">

        {/* Element tab — rules affecting the current selection */}
        {activeTab === 'element' && (() => {
          if (!selection) {
            return (
              <div className="text-center py-6">
                <p className="text-xs text-gray-400">Select a field, section, or form to see its rules.</p>
              </div>
            );
          }
          const selectionId = selection.level === 'field'
            ? (selection as { level: 'field'; fieldId: string }).fieldId
            : selection.level === 'section'
              ? (selection as { level: 'section'; sectionId: string }).sectionId
              : null;
          const elementRules = displayRules.filter(({ rule }) => {
            const cond = rule.when as { field?: string };
            if (selectionId && cond.field === selectionId) return true;
            return rule.then.some((a) => {
              const act = a as { formId?: string; sectionId?: string; fieldId?: string };
              return (selectionId && (act.sectionId === selectionId || act.fieldId === selectionId));
            });
          });
          if (elementRules.length === 0) {
            return (
              <div className="text-center py-6">
                <p className="text-xs text-gray-400">No rules affect the selected element.</p>
                <p className="text-[10px] text-gray-400 mt-1">Use Canvas Rules tab to create rules.</p>
              </div>
            );
          }
          return (
            <div className="space-y-2">
              {elementRules.map(({ rule, source, formId, formName }) => (
                <div key={`${source}:${formId ?? 'template'}:${rule.id}`} className="bg-white border border-gray-200 rounded-lg p-2.5 group hover:border-gray-300 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{rule.name || 'Unnamed Rule'}</div>
                      {source === 'form' && <div className="text-[10px] text-blue-500 mt-0.5">Form rule: {formName}</div>}
                      <div className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">
                        <span className="font-semibold text-gray-600">WHEN</span> {summarizeCondition(rule.when)}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        <span className="font-semibold text-gray-600">THEN</span> {rule.then.map(summarizeAction).join(', ')}
                      </div>
                    </div>
                    {source === 'template' && (
                      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setActiveTab('canvas'); openBuilder(rule); }} className="p-1 rounded-sm text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit rule">
                          <Pencil size={11} />
                        </button>
                        <button onClick={() => handleDeleteRule(rule.id)} className="p-1 rounded-sm text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Delete rule">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Canvas Rules tab */}
        {activeTab === 'canvas' && (
        <>
        {/* Need 2+ forms warning */}
        {formList.length < 2 && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-700">Add at least 2 forms to create cross-form rules.</p>
          </div>
        )}

        {/* Rule Builder — 4-step wizard */}
        {showBuilder && (() => {
          const STEPS = ['Trigger', 'Condition', 'Action', 'Save'] as const;
          const stepDone = {
            0: !!sourceFormId && !!sourceFieldId,
            1: conditionReady,
            2: actionTargetReady,
            3: true,
          } as Record<0 | 1 | 2 | 3, boolean>;
          const canGoNext = stepDone[wizardStep];
          return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
              {/* Header + step dots */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-blue-800">{editingRuleId ? 'Edit Rule' : 'New Rule'}</span>
                  <div className="flex items-center gap-1">
                    {STEPS.map((label, i) => (
                      <button
                        key={label}
                        onClick={() => { if (i < wizardStep || stepDone[i as 0 | 1 | 2 | 3]) setWizardStep(i as 0 | 1 | 2 | 3); }}
                        title={label}
                        className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center transition-colors ${
                          i === wizardStep
                            ? 'bg-blue-600 text-white'
                            : i < wizardStep
                              ? 'bg-blue-300 text-white'
                              : 'bg-blue-100 text-blue-400'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  <span className="text-[10px] text-blue-500">{STEPS[wizardStep]}</span>
                </div>
                <button onClick={resetBuilder} className="p-0.5 text-blue-400 hover:text-blue-600"><X size={12} /></button>
              </div>

              {/* Step 1 — Trigger: form + field */}
              {wizardStep === 0 && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Form</label>
                    <select
                      value={sourceFormId}
                      onChange={(e) => { setSourceFormId(e.target.value); setSourceFieldId(''); setConditionValue(''); }}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white"
                    >
                      <option value="">Select form…</option>
                      {formList.map((f) => <option key={f.id} value={f.id}>{f.form!.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Trigger field</label>
                    <select
                      value={sourceFieldId}
                      onChange={(e) => {
                        setSourceFieldId(e.target.value);
                        const f = sourceForm?.fields.find((ff) => ff.id === e.target.value);
                        setConditionValue(f?.type === 'toggle' ? false : '');
                        if (f?.type === 'number' || f?.type === 'measurement') setOperator('gt');
                        else setOperator('equals');
                      }}
                      disabled={!sourceFormId}
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) disabled:bg-gray-50 bg-white"
                    >
                      <option value="">Select field…</option>
                      {sourceFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                    {sourceFormId && sourceFields.length === 0 && (
                      <p className="text-[10px] text-amber-600 mt-1">No rule-eligible fields in this form (toggle, dropdown, number…)</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2 — Condition: operator + value */}
              {wizardStep === 1 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 bg-white border border-blue-200 rounded-sm text-[10px] text-gray-600">
                    <span className="font-medium truncate">{sourceForm?.name}</span>
                    <ChevronRight size={10} className="text-gray-400 shrink-0" />
                    <span className="font-medium truncate">{sourceField?.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Operator</label>
                      <select
                        value={operator}
                        onChange={(e) => {
                          const nextOperator = e.target.value as Operator;
                          setOperator(nextOperator);
                          setConditionValue(nextOperator === 'in' || nextOperator === 'notIn' ? [] : '');
                        }}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white"
                      >
                        {(Object.keys(OPERATOR_LABELS) as Operator[]).map((op) => (
                          <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Value</label>
                      {renderValuePicker() ?? (
                        <div className="w-full px-2 py-1 text-xs bg-gray-50 border border-gray-200 rounded-sm text-gray-400">—</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3 — Polymorphic action + target */}
              {wizardStep === 2 && (() => {
                const ACTION_GROUPS = [
                  { label: 'Form', actions: [{ value: 'hideForm', label: 'Hide Form' }, { value: 'showForm', label: 'Show Form' }] },
                  { label: 'Section', actions: [{ value: 'hideSection', label: 'Hide Section' }, { value: 'showSection', label: 'Show Section' }] },
                  { label: 'Field', actions: [{ value: 'hideField', label: 'Hide Field' }, { value: 'showField', label: 'Show Field' }, { value: 'require', label: 'Make Required' }, { value: 'disable', label: 'Disable Field' }, { value: 'enable', label: 'Enable Field' }, { value: 'setOptions', label: 'Set Options' }] },
                ] as const;
                const allSections = formList.flatMap((f) => (f.form?.layout.sections || []).map((s) => ({ sectionId: s.id, label: s.title || 'Untitled', formName: f.form!.name })));
                const allFields = formList.flatMap((f) => (f.form?.fields || []).map((ff) => ({ fieldId: ff.id, label: ff.label || '(untitled)', formName: f.form!.name })));
                return (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 mb-1">Action type</label>
                      {ACTION_GROUPS.map((group) => (
                        <div key={group.label} className="mb-1.5">
                          <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{group.label}</p>
                          <div className="flex flex-wrap gap-1">
                            {group.actions.map((a) => (
                              <button
                                key={a.value}
                                onClick={() => { setActionType(a.value as ActionType); setTargetFormId(''); setTargetFieldId(''); setTargetSectionId(''); setSetOptionsText(''); setRequireNotes(false); setRequirePhotos(false); }}
                                className={`px-2 py-1 text-[10px] rounded border font-medium transition-colors ${
                                  actionType === a.value
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {(actionType === 'hideForm' || actionType === 'showForm') && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Target form</label>
                        <select value={targetFormId} onChange={(e) => setTargetFormId(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white">
                          <option value="">Select form…</option>
                          {targetForms.map((f) => <option key={f.id} value={f.id}>{f.form!.name}</option>)}
                        </select>
                      </div>
                    )}
                    {(actionType === 'hideSection' || actionType === 'showSection') && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Target section</label>
                        <select value={targetSectionId} onChange={(e) => setTargetSectionId(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white">
                          <option value="">Select section…</option>
                          {allSections.map((s) => <option key={s.sectionId} value={s.sectionId}>{s.label} ({s.formName})</option>)}
                        </select>
                      </div>
                    )}
                    {(actionType === 'hideField' || actionType === 'showField' || actionType === 'require' || actionType === 'disable' || actionType === 'enable' || actionType === 'setOptions') && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Target field</label>
                        <select value={targetFieldId} onChange={(e) => setTargetFieldId(e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white">
                          <option value="">Select field…</option>
                          {allFields.map((f) => <option key={f.fieldId} value={f.fieldId}>{f.label} ({f.formName})</option>)}
                        </select>
                      </div>
                    )}
                    {actionType === 'require' && (
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-1.5 text-xs text-gray-600">
                          <input type="checkbox" checked={requireNotes} onChange={(event) => setRequireNotes(event.target.checked)} className="rounded-sm border-gray-300" />
                          Require notes
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-gray-600">
                          <input type="checkbox" checked={requirePhotos} onChange={(event) => setRequirePhotos(event.target.checked)} className="rounded-sm border-gray-300" />
                          Require photos
                        </label>
                      </div>
                    )}
                    {actionType === 'setOptions' && (
                      <div>
                        <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Replacement options</label>
                        <textarea
                          value={setOptionsText}
                          onChange={(event) => setSetOptionsText(event.target.value)}
                          rows={4}
                          placeholder={'pass|Pass\nfail|Fail'}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white resize-none"
                        />
                        <p className="mt-1 text-[10px] text-gray-400">One option per line. Use value|Label when value differs from label.</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Step 4 — Name + summary + save */}
              {wizardStep === 3 && (
                <div className="space-y-2">
                  <div className="px-2 py-2 bg-white border border-blue-200 rounded-sm text-[10px] text-gray-600 space-y-1">
                    <div><span className="font-semibold text-gray-700">WHEN</span> {sourceField?.label} ({sourceForm?.name}) {OPERATOR_LABELS[operator]} {Array.isArray(conditionValue) ? conditionValue.join(', ') : String(conditionValue)}</div>
                    <div><span className="font-semibold text-gray-700">THEN</span> {actionType}{' '}
                      {(actionType === 'hideForm' || actionType === 'showForm') && `"${formList.find((f) => f.id === targetFormId)?.form?.name || targetFormId}"`}
                      {(actionType === 'hideSection' || actionType === 'showSection') && `section "${targetSectionId}"`}
                      {(actionType === 'hideField' || actionType === 'showField' || actionType === 'require' || actionType === 'disable' || actionType === 'enable' || actionType === 'setOptions') && `field "${formList.flatMap((f) => f.form?.fields || []).find((ff) => ff.id === targetFieldId)?.label || targetFieldId}"`}
                      {actionType === 'setOptions' && ` (${parseOptionsText(setOptionsText).length} options)`}
                      {actionType === 'require' && (requireNotes || requirePhotos) && ` (${[requireNotes ? 'notes' : '', requirePhotos ? 'photos' : ''].filter(Boolean).join(', ')})`}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Rule name <span className="text-gray-400">(optional)</span></label>
                    <input
                      autoFocus
                      type="text"
                      value={ruleName}
                      onChange={(e) => setRuleName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRule(); }}
                      placeholder="e.g. Hide sprinkler form if no sprinklers…"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white"
                    />
                  </div>
                </div>
              )}

              {/* Navigation footer */}
              <div className="flex items-center justify-between border-t border-blue-200 pt-2">
                <button
                  onClick={() => wizardStep > 0 ? setWizardStep((wizardStep - 1) as 0 | 1 | 2 | 3) : resetBuilder()}
                  className="px-2.5 py-1 text-xs text-gray-600 border border-gray-300 rounded-sm hover:bg-gray-50"
                >
                  {wizardStep === 0 ? 'Cancel' : 'Back'}
                </button>
                {wizardStep < 3 ? (
                  <button
                    onClick={() => setWizardStep((wizardStep + 1) as 0 | 1 | 2 | 3)}
                    disabled={!canGoNext}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white rounded-sm disabled:opacity-40"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    Next <ChevronRight size={10} />
                  </button>
                ) : (
                  <button
                    onClick={handleSaveRule}
                    disabled={!canSave}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white rounded-sm disabled:opacity-50"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    <Check size={11} /> {editingRuleId ? 'Update' : 'Save Rule'}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Existing Rules list */}
        {displayRules.length === 0 && !showBuilder ? (
          <div className="text-center py-6">
            <p className="text-xs text-gray-400 mb-1">No rules defined</p>
            <p className="text-[10px] text-gray-400">Rules hide or show forms in real-time as inspectors fill out fields.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayRules.map(({ rule, source, formId, formName }) => (
              <div key={`${source}:${formId ?? 'template'}:${rule.id}`} className="bg-white border border-gray-200 rounded-lg p-2.5 group hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 truncate">{rule.name || 'Unnamed Rule'}</div>
                    {source === 'form' && <div className="text-[10px] text-blue-500 mt-0.5">Form rule: {formName}</div>}
                    <div className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">
                      <span className="font-semibold text-gray-600">WHEN</span> {summarizeCondition(rule.when)}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      <span className="font-semibold text-gray-600">THEN</span> {rule.then.map(summarizeAction).join(', ')}
                    </div>
                  </div>
                  {source === 'template' && (
                    <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openBuilder(rule)}
                        className="p-1 rounded-sm text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Edit rule"
                      >
                        <Pencil size={11} />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        className="p-1 rounded-sm text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete rule"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="pt-1 flex items-start gap-1 text-[10px] text-gray-400">
          <Info size={10} className="shrink-0 mt-0.5" />
          <span>Rules are evaluated in real-time. Hidden forms are automatically skipped.</span>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

export default CanvasRulesPanel;
